/**
 * AmbulanceMiniTracker — inline Leaflet map that shows an in-flight
 * ambulance approaching the pickup point in real time.
 *
 * Polls `GET /me/health/bookings/{id}/tracking` every 15 seconds while
 * mounted (via useInterval) and re-renders the vehicle marker + polyline
 * track. Falls back to showing just the pickup marker + partner base when
 * no vehicle location has been pushed yet (nice for pre-dispatch state).
 *
 * Props:
 *   bookingId : string — health_bookings.id (type='ambulance')
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import { NavigationArrow } from "@phosphor-icons/react";
import { custApi } from "../lib/customerApi";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Vehicle marker — red pulsing dot with a heading arrow.
const vehicleIcon = (heading = 0) => new L.DivIcon({
  className: "",
  html: `
    <div style="position:relative;width:30px;height:30px;">
      <span style="position:absolute;inset:0;border-radius:50%;background:rgba(220,38,38,.28);animation:amb-track-pulse 1.4s ease-out infinite;"></span>
      <div style="position:relative;width:30px;height:30px;background:#DC2626;border:3px solid white;border-radius:50%;
                   box-shadow:0 4px 14px rgba(220,38,38,.55);display:flex;align-items:center;justify-content:center;
                   transform:rotate(${heading}deg);">
        <span style="color:white;font-size:14px;font-weight:900;">▲</span>
      </div>
    </div>
    <style>@keyframes amb-track-pulse{0%{transform:scale(1);opacity:.85}100%{transform:scale(2.2);opacity:0}}</style>`,
  iconSize: [30, 30], iconAnchor: [15, 15],
});

const pickupIcon = new L.DivIcon({
  className: "",
  html: `<div style="width:22px;height:22px;background:#1D4ED8;border:3px solid white;border-radius:50%;
                    box-shadow:0 3px 10px rgba(29,78,216,.55);"></div>`,
  iconSize: [22, 22], iconAnchor: [11, 11],
});

const partnerIcon = new L.DivIcon({
  className: "",
  html: `<div style="width:20px;height:20px;background:#15803D;border:2px solid white;border-radius:4px;
                    box-shadow:0 3px 8px rgba(21,128,61,.45);"></div>`,
  iconSize: [20, 20], iconAnchor: [10, 10],
});

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points?.length) return;
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 });
  }, [points, map]);
  return null;
}

export default function AmbulanceMiniTracker({ bookingId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const timer = useRef(null);

  const load = async () => {
    try {
      const r = await custApi.get(`/me/health/bookings/${bookingId}/tracking`);
      setData(r.data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Tracking unavailable");
    }
  };

  useEffect(() => {
    load();
    timer.current = setInterval(load, 15000);
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [bookingId]);

  const points = useMemo(() => {
    if (!data) return [];
    const pts = [];
    if (data.vehicle_location) pts.push([data.vehicle_location.lat, data.vehicle_location.lng]);
    if (data.pickup?.lat != null) pts.push([data.pickup.lat, data.pickup.lng]);
    if (data.partner?.lat != null) pts.push([data.partner.lat, data.partner.lng]);
    return pts;
  }, [data]);

  const track = useMemo(() => {
    if (!data?.vehicle_track?.length) return null;
    return data.vehicle_track.map(t => [t.lat, t.lng]);
  }, [data]);

  if (err || !data) return null;
  if (points.length < 2) {
    // Not enough coords to render — likely a pre-mapping ambulance booking.
    return (
      <div className="border-t border-blue-100 px-4 py-2.5 text-[11px] text-[#5C5A55] bg-[#F8FAFC]"
            data-testid={`amb-track-empty-${bookingId}`}>
        Live tracking will appear once the vehicle starts moving.
      </div>
    );
  }

  const eta = data.eta;
  const veh = data.vehicle_location;
  const center = veh || (data.pickup?.lat != null ? data.pickup : data.partner);

  return (
    <div className="border-t border-blue-100" data-testid={`amb-track-${bookingId}`}>
      <div className="flex items-center gap-2 px-4 py-2 bg-white">
        <div className="w-6 h-6 rounded-full bg-[#DC2626] text-white flex items-center justify-center animate-pulse">
          <NavigationArrow size={12} weight="fill"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#7F1D1D]">
            Live tracker
          </div>
          <div className="text-[11px] text-[#111827] font-semibold">
            {eta
              ? <>Vehicle is <span className="text-[#DC2626]">{eta.distance_km} km · ~{Math.round(eta.duration_min)} min</span> away</>
              : (veh ? "Vehicle en-route" : "Awaiting driver dispatch")
            }
          </div>
        </div>
      </div>
      <div style={{ height: 180, width: "100%" }}
            data-testid={`amb-track-map-${bookingId}`}>
        <MapContainer
          center={[center.lat, center.lng]}
          zoom={13}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom={false}
          dragging={true}
          doubleClickZoom={false}
          attributionControl={false}
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"/>
          {track && track.length >= 2 && (
            <Polyline positions={track} pathOptions={{ color: "#DC2626", weight: 4, opacity: 0.75 }}/>
          )}
          {data.partner?.lat != null && (
            <Marker position={[data.partner.lat, data.partner.lng]} icon={partnerIcon}>
              <Popup>{data.partner.name || "Ambulance base"}</Popup>
            </Marker>
          )}
          {data.pickup?.lat != null && (
            <Marker position={[data.pickup.lat, data.pickup.lng]} icon={pickupIcon}>
              <Popup>Pickup · {data.pickup.address || "here"}</Popup>
            </Marker>
          )}
          {veh && (
            <Marker position={[veh.lat, veh.lng]} icon={vehicleIcon(veh.heading || 0)}>
              <Popup>Ambulance · updated {new Date(veh.ts).toLocaleTimeString()}</Popup>
            </Marker>
          )}
          <FitBounds points={points}/>
        </MapContainer>
      </div>
    </div>
  );
}
